import React, { Component } from 'react'
import { Editor, EditorState, RichUtils } from 'draft-js'
import { setNarrative } from '../actions/narrative'
import { connect } from 'react-redux'
import { convertFromHTML, convertToHTML } from 'draft-convert'
import ButtonPanel from './ButtonPanel'
import Comments from './Comments'
import EditMode from './EditMode'
import { setModal } from '../actions/appSettings'

class Draft extends Component {
  state = {
    loading: true,
  }

  MAX_LENGTH = 2500

  setDomEditorRef = ref => (this.domEditor = ref)

  onChange = (editorState, callback = null) => {
    this.setState({ editorState }, callback)
  }

  setEditorStateFromProps = () => {
    let newState
    if (this.props.narrative[this.props.currentTab]) {
      let contentState = convertFromHTML(this.props.narrative[this.props.currentTab])
      newState = EditorState.createWithContent(contentState)
    } else {
      newState = EditorState.createEmpty()
    }
    this.setState({
      editorState: newState,
    })
  }

  componentDidUpdate(previousProps, prevState) {
    if (previousProps.narrative !== this.props.narrative && this.state.editorState) {
      this.setEditorStateFromProps()
    }
  }

  componentWillMount() {
    this.setEditorStateFromProps()
  }

  reserializeNarrative = () => {
    const html = convertToHTML(this.state.editorState.getCurrentContent())
    return { ...this.props.narrative, [this.props.currentTab]: html }
  }

  serializeToStore = () => {
    this.props.setNarrative(this.reserializeNarrative())
  }

  render() {
    return (
      <div className='draft'>
        <div className='draft--content' onBlur={this.serializeToStore}>
          <EditMode />
          <div className='draft--menu'>
            <InlineStyleControls editorState={this.state.editorState} onToggle={this.toggleInlineStyle} />
            <BlockStyleControls editorState={this.state.editorState} onToggle={this.toggleBlockType} />
          </div>

          <Editor
            className='draft--editor'
            editorState={this.state.editorState}
            handleKeyCommand={this.handleKeyCommand}
            onChange={this.onChange}
            onBlur={this.serializeToStore}
            handleBeforeInput={this._handleBeforeInput}
            handlePastedText={this._handlePastedText}
            placeholder='Please type your text here...'
            ref={this.setDomEditorRef}
            readOnly={!this.props.narrative.user_can_save || this.props.narrativeLoading}
            data-testid='textarea'
            spellCheck={true}
          />
        </div>

        <Comments />
        <ButtonPanel wrapperClassName='draft--submission' />
      </div>
    )
  }

  _getLengthOfSelectedText = () => {
    const currentSelection = this.state.editorState.getSelection()
    const isCollapsed = currentSelection.isCollapsed()

    let length = 0

    if (!isCollapsed) {
      const currentContent = this.state.editorState.getCurrentContent()
      const startKey = currentSelection.getStartKey()
      const endKey = currentSelection.getEndKey()
      const startBlock = currentContent.getBlockForKey(startKey)
      const isStartAndEndBlockAreTheSame = startKey === endKey
      const startBlockTextLength = startBlock.getLength()
      const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset()
      const endSelectedTextLength = currentSelection.getEndOffset()
      const keyAfterEnd = currentContent.getKeyAfter(endKey)
      if (isStartAndEndBlockAreTheSame) {
        length += currentSelection.getEndOffset() - currentSelection.getStartOffset()
      } else {
        let currentKey = startKey

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1
          } else if (currentKey === endKey) {
            length += endSelectedTextLength
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1
          }
          currentKey = currentContent.getKeyAfter(currentKey)
        }
      }
    }
    return length
  }

  _handleBeforeInput = () => {
    const currentContent = this.state.editorState.getCurrentContent()
    const currentContentLength = currentContent.getPlainText('').length
    const selectedTextLength = this._getLengthOfSelectedText()

    if (currentContentLength - selectedTextLength > this.MAX_LENGTH - 1) {
      this.props.setModal(`You can enter maximum ${this.MAX_LENGTH} characters.`)
      return 'handled'
    }
  }

  _handlePastedText = pastedText => {
    const currentContent = this.state.editorState.getCurrentContent()
    const currentContentLength = currentContent.getPlainText('').length
    const selectedTextLength = this._getLengthOfSelectedText()

    if (currentContentLength + pastedText.length - selectedTextLength > this.MAX_LENGTH) {
      this.props.setModal(`You can enter maximum ${this.MAX_LENGTH} characters.`)
      return 'handled'
    }
  }

  toggleInlineStyle = inlineStyle => {
    // buttons are not disabled when editor is, so block them here and below
    if (this.props.narrative.user_can_save) {
      // buttons can be clicked without triggering editor.onBlur (which serializes to store), so serialize to store here too
      this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle), this.serializeToStore)
    }
  }

  toggleBlockType = blockType => {
    if (this.props.narrative.user_can_save) {
      this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType), this.serializeToStore)
    }
  }
}

const BlockStyleControls = props => {
  const BLOCK_TYPES = [{ label: 'List', style: 'unordered-list-item' }]
  const { editorState } = props
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()
  return (
    <div className='RichEditor-controls'>
      {BLOCK_TYPES.map(type => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  )
}

const InlineStyleControls = props => {
  const INLINE_STYLES = [
    { label: 'Bold', style: 'BOLD' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' },
  ]
  return (
    <div className='RichEditor-controls'>
      {INLINE_STYLES.map(type => (
        <StyleButton
          key={type.label}
          active={props.editorState.getCurrentInlineStyle().has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  )
}

class StyleButton extends React.Component {
  constructor(props) {
    super(props)
    this.onToggle = e => {
      e.preventDefault()
      props.onToggle(props.style)
    }
  }
  render() {
    let className = 'RichEditor-styleButton'
    if (this.props.active) {
      className += ' RichEditor-activeButton'
    }
    return (
      <span className={className} onMouseDown={this.onToggle}>
        {this.props.label}
      </span>
    )
  }
}

const mapStateToProps = state => ({
  narrative: state.narrative.narrative,
  narrativeLoading: state.narrative.narrativeLoading,
  currentTab: state.appSettings.currentTab,
  userRole: state.appSettings.userInfo ? state.appSettings.userInfo.role : null,
})

const mapDispatchToProps = { setNarrative, setModal }

Draft = connect(
  mapStateToProps,
  mapDispatchToProps
)(Draft)

export default Draft
